import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { optimizeImage } from "@/lib/image";
import { money, padNum } from "@/lib/format";
import CountdownTimer from "@/components/CountdownTimer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { QRCodeCanvas } from "qrcode.react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import {
  Shuffle, Minus, Plus, Ticket, Trophy, Clock, Upload, CheckCircle2, ShieldCheck,
  Loader2, ChevronLeft, ChevronRight, Sparkles, ScrollText, Users, FileCheck, User, Mail, Phone, X,
  Grid3x3, Wand2, Wallet, MessageCircle, Copy, Check, Repeat,
} from "lucide-react";

function CopyableSinpe({ value, brand }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Número SINPE copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("No se pudo copiar");
    }
  };
  return (
    <button type="button" onClick={copy} data-testid="copy-sinpe-btn"
      className="mt-2 w-full flex items-center justify-between gap-2 rounded-xl border-2 border-dashed bg-white px-3.5 py-2.5 hover:bg-neutral-50 transition-colors"
      style={{ borderColor: `${brand}55` }}>
      <span className="min-w-0 text-left">
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-neutral-400">SINPE Móvil</span>
        <span className="block font-display font-extrabold text-lg tabular-nums truncate" style={{ color: brand }}>{value}</span>
      </span>
      <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white flex-shrink-0" style={{ background: brand }}>
        {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar</>}
      </span>
    </button>
  );
}

const ORDER_STATUS = {
  reserved: { label: "Reservado (falta subir comprobante)", cls: "bg-neutral-100 text-neutral-600", dot: "bg-neutral-400" },
  pending: { label: "En revisión ⏳", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  approved: { label: "Aprobado ✅", cls: "bg-[#E6F7FE] text-[#0092CC]", dot: "bg-[#00AEEF]" },
  rejected: { label: "Rechazado", cls: "bg-red-100 text-red-700", dot: "bg-red-500" },
  expired: { label: "Reserva vencida", cls: "bg-neutral-100 text-neutral-500", dot: "bg-neutral-400" },
};

export default function PublicRaffle() {
  const { raffleId } = useParams();
  const [raffle, setRaffle] = useState(null);
  const [error, setError] = useState(false);
  const [mode, setMode] = useState("auto"); // 'auto' | 'manual'
  const [qty, setQty] = useState(1);
  const [picked, setPicked] = useState([]);
  const [reshuffles, setReshuffles] = useState(0);
  const [picking, setPicking] = useState(false);
  const [drawerStep, setDrawerStep] = useState(null); // 'checkout' | 'done'
  const [buyer, setBuyer] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "" });
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [file, setFile] = useState(null);
  const [slide, setSlide] = useState(0);
  const [grid, setGrid] = useState(null);
  const [gridPage, setGridPage] = useState(0);
  const [gridLoading, setGridLoading] = useState(false);
  const [tab, setTab] = useState("comprar");
  const [lookupQ, setLookupQ] = useState("");
  const [lookupMode, setLookupMode] = useState("correo");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResults, setLookupResults] = useState(null);
  const [liveProgress, setLiveProgress] = useState(null);
  const [mirrors, setMirrors] = useState([]);
  const mirrorFetched = useRef(false);
  const lastAvailRef = useRef(null);
  const gridPageRef = useRef(0);
  const raffleRef = useRef(null);

  const doLookup = async () => {
    const val = lookupQ.trim();
    if (!val) { toast.error(lookupMode === "telefono" ? "Ingresa tu teléfono" : "Ingresa tu correo"); return; }
    if (lookupMode === "telefono") {
      const digits = val.replace(/\D/g, "");
      if (digits.length < 8) { toast.error("El teléfono debe tener al menos 8 dígitos"); return; }
    }
    setLookupLoading(true);
    try {
      const res = await api.get(`/public/raffles/${raffleId}/lookup`, { params: { q: val } });
      setLookupResults(res.data.results);
      if (res.data.results.length === 0) toast.info("No encontramos números con ese dato");
    } catch (e) {
      toast.error("No se pudo consultar. Intenta de nuevo.");
      setLookupResults([]);
    } finally { setLookupLoading(false); }
  };

  const load = useCallback(async () => {
    try { const res = await api.get(`/public/raffles/${raffleId}`); setRaffle(res.data); raffleRef.current = res.data; }
    catch (e) { setError(true); }
  }, [raffleId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (raffle) {
      document.documentElement.style.setProperty("--brand-primary", raffle.brand_color || "#059669");
      setQty((q) => Math.max(q, raffle.min_purchase || 1));
    }
  }, [raffle]);

  const images = raffle?.image_urls || [];
  const minp = raffle?.min_purchase || 1;
  const maxp = raffle?.max_purchase || null;
  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % images.length), 4500);
    return () => clearInterval(id);
  }, [images.length]);

  const loadGrid = useCallback(async (block, silent = false) => {
    if (!silent) setGridLoading(true);
    try {
      const total_numbers = raffleRef.current?.total_numbers || 0;
      const perBlock = total_numbers > 1000 ? 1000 : 100;
      const apiSize = Math.min(perBlock, 500);
      const callsPerBlock = Math.ceil(perBlock / apiSize); // 1 (chico) o 2 (grande)
      const startApiPage = block * callsPerBlock;
      const first = await api.get(`/public/raffles/${raffleId}/numbers`, { params: { page: startApiPage, size: apiSize, available_only: true } });
      const total = first.data.total;
      const totalBlocks = Math.max(1, Math.ceil(total / perBlock));
      if (block > totalBlocks - 1) { return loadGrid(totalBlocks - 1, silent); }
      let numbers = first.data.numbers;
      const lastValidApiPage = Math.max(0, Math.ceil(total / apiSize) - 1);
      const extraPages = [];
      for (let k = 1; k < callsPerBlock; k++) {
        const ap = startApiPage + k;
        if (ap <= lastValidApiPage) extraPages.push(ap);
      }
      if (extraPages.length) {
        const extra = await Promise.all(extraPages.map((ap) => api.get(`/public/raffles/${raffleId}/numbers`, { params: { page: ap, size: apiSize, available_only: true } })));
        extra.forEach((r) => { numbers = numbers.concat(r.data.numbers); });
      }
      setGrid({ total, page: block, size: perBlock, numbers });
      setGridPage(block);
      gridPageRef.current = block;
      lastAvailRef.current = total;
    } catch (e) { if (!silent) toast.error("No se pudo cargar la cuadrícula"); }
    finally { if (!silent) setGridLoading(false); }
  }, [raffleId]);

  // Grid + progreso en vivo: refresca solo cuando cambia la disponibilidad
  useEffect(() => {
    if (tab !== "comprar" || raffle?.status !== "active") return;
    lastAvailRef.current = null;
    const id = setInterval(async () => {
      if (document.hidden) return;
      try {
        const res = await api.get(`/public/raffles/${raffleId}/availability`);
        if (res.data.progress) setLiveProgress(res.data.progress);
        if (lastAvailRef.current !== null && res.data.available !== lastAvailRef.current && mode === "manual") {
          loadGrid(gridPageRef.current, true);
        }
        lastAvailRef.current = res.data.available;
      } catch (e) { console.error("Polling de disponibilidad falló:", e); }
    }, 7000);
    return () => clearInterval(id);
  }, [tab, mode, raffle?.status, raffleId, loadGrid]);

  // Números invertidos disponibles (se consultan una vez al abrir el checkout)
  useEffect(() => {
    if (drawerStep !== "checkout") { mirrorFetched.current = false; setMirrors([]); return; }
    if (mirrorFetched.current || picked.length === 0) return;
    mirrorFetched.current = true;
    api.get(`/public/raffles/${raffleId}/mirrors`, { params: { numbers: picked.join(",") } })
      .then((r) => setMirrors(r.data.mirrors || []))
      .catch(() => setMirrors([]));
  }, [drawerStep, picked, raffleId]);

  if (error) return <Centered><p className="text-neutral-500">Evento no encontrado.</p></Centered>;
  if (!raffle) return <Centered><Loader2 className="animate-spin text-[#00AEEF]" size={32} /></Centered>;

  const cur = raffle.currency || "₡";
  const brand = raffle.brand_color || "#059669";
  const p = liveProgress || raffle.progress || {};
  const isActive = raffle.status === "active";
  const drawMethodLabel = raffle.draw_method === "random" ? "Aleatorio verificable"
    : raffle.draw_method === "manual" ? "Manual" : "Vinculado a Lotería Nacional";
  const waDigits = (raffle.whatsapp || "").replace(/\D/g, "");
  const ctaCount = mode === "manual" ? picked.length : qty;

  const priceFor = (n) => {
    const packages = [...(raffle.packages || [])].sort((a, b) => b.quantity - a.quantity);
    let rem = n, total = 0;
    for (const pk of packages) { while (rem >= pk.quantity && pk.quantity > 0) { total += pk.price; rem -= pk.quantity; } }
    total += rem * raffle.price_per_number;
    return total;
  };

  const doPick = async (isReshuffle) => {
    setPicking(true);
    try {
      const res = await api.get(`/public/raffles/${raffleId}/pick`, { params: { count: qty } });
      setPicked(res.data.numbers);
      if (isReshuffle) setReshuffles((r) => r + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "No hay números disponibles"); }
    finally { setPicking(false); }
  };

  const selectPackage = (quantity) => { setQty(quantity); setPicked([]); setReshuffles(0); };

  const switchMode = (m) => {
    setMode(m); setPicked([]); setReshuffles(0);
    if (m === "manual" && !grid) loadGrid(0);
  };

  const toggleNum = (n, status) => {
    if (status !== "available") return;
    const has = picked.includes(n);
    if (!has && maxp && picked.length >= maxp) { toast.error(`Máximo ${maxp} números por compra`); return; }
    setPicked((prev) => has ? prev.filter((x) => x !== n) : [...prev, n]);
  };

  const openCheckout = async () => {
    let sel = picked;
    if (mode === "manual") {
      if (sel.length === 0) return toast.error("Selecciona al menos un número");
    } else if (sel.length === 0) {
      await doPick(false);
      sel = null; // picked updated async; validate on server + finalize
    }
    const count = sel ? sel.length : qty;
    if (count < minp) return toast.error(`La compra mínima es de ${minp} número(s)`);
    if (maxp && count > maxp) return toast.error(`La compra máxima es de ${maxp} número(s)`);
    setDrawerStep("checkout");
  };

  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const toggleMirror = (m) => {
    if (picked.includes(m)) { setPicked((prev) => prev.filter((x) => x !== m)); return; }
    if (maxp && picked.length >= maxp) { toast.error(`Máximo ${maxp} números por compra`); return; }
    setPicked((prev) => [...prev, m]);
  };

  const finalizePurchase = async () => {
    if (submittingRef.current) return;
    if (!buyer.buyer_name.trim()) return toast.error("Ingresa tu nombre completo");
    if (!emailValid(buyer.buyer_email)) return toast.error("Ingresa un correo válido");
    if (!buyer.buyer_phone.trim()) return toast.error("Ingresa tu número de teléfono");
    if (!file) return toast.error("Debes subir el comprobante de pago para finalizar");
    submittingRef.current = true;
    setSubmitting(true);
    try {
      let orderId = order?.order_id;
      if (!orderId) {
        const res = await api.post(`/public/raffles/${raffleId}/reserve`, { numbers: picked, ...buyer });
        setOrder(res.data);
        orderId = res.data.order_id;
      }
      const optimized = await optimizeImage(file);
      const fd = new FormData();
      fd.append("file", optimized);
      await api.post(`/public/orders/${orderId}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("¡Compra registrada! Tu pago está en verificación.");
      setDrawerStep("done");
      load();
    } catch (e) {
      const status = e.response?.status;
      toast.error(e.response?.data?.detail || e.message || "No se pudo completar la compra");
      if (status === 409) { setPicked([]); setOrder(null); load(); if (mode === "manual") loadGrid(gridPage); setDrawerStep(null); }
    } finally { setSubmitting(false); submittingRef.current = false; }
  };

  const closeDrawer = () => {
    setDrawerStep(null);
    if (order) { setPicked([]); setReshuffles(0); setOrder(null); setFile(null); setBuyer({ buyer_name: "", buyer_email: "", buyer_phone: "" }); load(); if (mode === "manual") loadGrid(gridPage); }
  };

  const numState = (status, selected) => {
    if (selected) return { background: brand, color: "#fff", borderColor: brand };
    if (status === "sold") return { background: "#fee2e2", color: "#b91c1c", borderColor: "#fecaca", textDecoration: "line-through", opacity: 0.6 };
    if (status === "pending") return { background: "#e0e7ff", color: "#4338ca", borderColor: "#c7d2fe", opacity: 0.75 };
    if (status === "reserved") return { background: "#fef3c7", color: "#b45309", borderColor: "#fde68a", opacity: 0.7 };
    return { background: "#fafafa", color: "#404040", borderColor: "#e5e5e5" };
  };
  const totalPages = grid ? Math.ceil(grid.total / grid.size) : 0;

  return (
    <div className="max-w-lg mx-auto w-full bg-white min-h-screen shadow-xl relative pb-28" style={{ "--brand": brand }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl border-b px-4 h-16 flex items-center gap-3"
        style={{ background: `${brand}F2`, borderColor: "rgba(255,255,255,0.15)" }}>
        {raffle.brand_logo_url
          ? <img src={raffle.brand_logo_url} alt="" className="h-9 w-9 rounded-xl object-cover ring-2 ring-white/40" />
          : <div className="h-9 w-9 rounded-xl flex items-center justify-center bg-white/20 text-white"><Ticket size={18} /></div>}
        <span className="font-display font-extrabold text-white truncate text-lg">{raffle.brand_name}</span>
      </header>

      {/* Hero carousel */}
      <div className="relative">
        {images.length > 0 ? (
          <div className="relative h-80 sm:h-96 overflow-hidden bg-neutral-900">
            {images.map((u, i) => (
              <img key={u} src={u} alt="" data-testid={`hero-image-${i}`}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                style={{ opacity: i === slide ? 1 : 0 }} />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            {images.length > 1 && (
              <>
                <button data-testid="hero-prev" onClick={() => setSlide((s) => (s - 1 + images.length) % images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/25 backdrop-blur text-white flex items-center justify-center hover:bg-white/40 transition-colors"><ChevronLeft size={20} /></button>
                <button data-testid="hero-next" onClick={() => setSlide((s) => (s + 1) % images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/25 backdrop-blur text-white flex items-center justify-center hover:bg-white/40 transition-colors"><ChevronRight size={20} /></button>
                <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setSlide(i)} className="h-2 rounded-full transition-all"
                      style={{ width: i === slide ? 22 : 8, background: i === slide ? "#fff" : "rgba(255,255,255,0.5)" }} />
                  ))}
                </div>
              </>
            )}
            <div className="absolute bottom-4 left-4 right-4">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-white px-2.5 py-1 rounded-full mb-2" style={{ background: brand }}>
                <Sparkles size={12} /> Evento activo
              </span>
              <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-white drop-shadow-lg leading-tight">{raffle.title}</h1>
            </div>
          </div>
        ) : (
          <div className="h-72 sm:h-80 flex flex-col items-center justify-center text-white p-6" style={{ background: `linear-gradient(135deg, ${brand}, ${brand}CC)` }}>
            <Ticket size={40} className="mb-2 opacity-90" />
            <h1 className="font-display font-extrabold text-2xl text-center">{raffle.title}</h1>
          </div>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 px-4 pt-3 overflow-x-auto no-scrollbar">
          {images.map((u, i) => (
            <button key={i} onClick={() => setSlide(i)} className="flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all"
              style={{ borderColor: i === slide ? brand : "transparent", opacity: i === slide ? 1 : 0.6 }}>
              <img src={u} alt="" className="h-14 w-20 object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Tabs: Comprar / Mis números (sticky, never overlaps bottom CTA) */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur px-5 pt-3 pb-2 border-b border-neutral-100">
        <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-neutral-100" data-testid="public-tabs">
          <button data-testid="tab-comprar" onClick={() => setTab("comprar")}
            className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all"
            style={tab === "comprar" ? { background: "#fff", color: brand, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" } : { color: "#737373" }}>
            <Ticket size={16} /> Comprar
          </button>
          <button data-testid="tab-estado" onClick={() => setTab("estado")}
            className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all"
            style={tab === "estado" ? { background: "#fff", color: brand, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" } : { color: "#737373" }}>
            <FileCheck size={16} /> Mis números
          </button>
        </div>
      </div>

      <div className="p-5">
        {raffle.description && <p className="text-neutral-600 text-[15px] leading-relaxed whitespace-pre-line">{raffle.description}</p>}

        {raffle.status === "drawn" && raffle.winning_number != null && (
          <div className="mt-4 rounded-2xl p-4 bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-200 flex items-center gap-3" data-testid="public-winner">
            <div className="h-12 w-12 rounded-2xl bg-amber-400 text-amber-950 flex items-center justify-center"><Trophy size={24} /></div>
            <div><p className="text-sm text-amber-800">Número ganador</p><p className="font-display font-extrabold text-3xl text-amber-900">#{raffle.winning_number}</p></div>
          </div>
        )}

        <div className="mt-5 rounded-2xl p-4 text-white" style={{ background: `linear-gradient(135deg, ${brand}, ${brand}D0)` }}>
          <div className="flex items-center gap-2 text-sm font-semibold mb-3 text-white/90"><Clock size={16} /> Cierra en</div>
          <CountdownTimer target={raffle.draw_date} />
        </div>

        <div className="mt-5 rounded-2xl border border-neutral-200 p-5 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
          <div className="flex items-end justify-between mb-2">
            <div className="flex items-center gap-1.5 text-sm text-neutral-500"><Users size={15} /> Progreso del evento</div>
            <span className="font-display font-extrabold text-2xl" style={{ color: brand }}>{p.pct ?? 0}%</span>
          </div>
          <div className="h-3.5 rounded-full bg-neutral-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ${(p.pct ?? 0) > 90 ? "animate-pulse-soft" : ""}`}
              style={{ width: `${p.pct ?? 0}%`, background: `linear-gradient(90deg, ${brand}, ${brand}AA)` }} />
          </div>
          <p className="text-sm text-neutral-500 mt-2"><b className="text-neutral-800">{p.sold ?? 0}</b> de {p.total ?? raffle.total_numbers} números ya tienen dueño</p>
        </div>

        {/* Tabs moved to sticky bar above */}

        {tab === "estado" && (
          <div className="mt-5" data-testid="estado-panel">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white" style={{ background: brand }}><FileCheck size={16} /></div>
              <Label className="font-display font-extrabold text-lg">Verifica el estado de tus números</Label>
            </div>
            <p className="text-sm text-neutral-500 mt-1">Elige cómo consultar e ingresa el <b>correo</b> o <b>teléfono</b> que usaste al comprar para ver si tus números ya fueron aprobados.</p>
            <div className="flex gap-2 mt-4">
              <Select value={lookupMode} onValueChange={(v) => { setLookupMode(v); setLookupResults(null); }}>
                <SelectTrigger data-testid="lookup-mode" className="w-[130px] rounded-xl shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="correo" data-testid="lookup-mode-correo">Correo</SelectItem>
                  <SelectItem value="telefono" data-testid="lookup-mode-telefono">Teléfono</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="lookup-input" value={lookupQ} onChange={(e) => setLookupQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doLookup()}
                type={lookupMode === "telefono" ? "tel" : "email"}
                inputMode={lookupMode === "telefono" ? "numeric" : "email"}
                placeholder={lookupMode === "telefono" ? "8888-8888 (mín. 8 dígitos)" : "tucorreo@ejemplo.com"} className="rounded-xl flex-1" />
              <Button data-testid="lookup-btn" onClick={doLookup} disabled={lookupLoading} className="rounded-xl px-5" style={{ background: brand }}>
                {lookupLoading ? <Loader2 size={16} className="animate-spin" /> : "Consultar"}
              </Button>
            </div>

            {lookupResults !== null && (
              <div className="mt-5 space-y-3" data-testid="lookup-results">
                {lookupResults.length === 0 ? (
                  <div className="rounded-2xl bg-neutral-50 p-6 text-center text-neutral-500 text-sm" data-testid="lookup-empty">
                    No encontramos compras con ese dato en este evento. Verifica que sea el mismo dato que usaste al comprar.
                  </div>
                ) : lookupResults.map((o) => {
                  const st = ORDER_STATUS[o.status] || ORDER_STATUS.pending;
                  return (
                    <div key={o.order_id} data-testid={`lookup-order-${o.order_id}`} className="rounded-2xl border border-neutral-200 p-4 bg-white">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-sm font-semibold text-neutral-700 truncate">{o.buyer_name}</span>
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`} data-testid={`lookup-status-${o.order_id}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} /> {st.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[...o.numbers].sort((a, b) => a - b).map((n) => (
                          <span key={n} className="text-sm font-bold px-2.5 py-1 rounded-lg text-white" style={{ background: brand }}>#{padNum(n)}</span>
                        ))}
                      </div>
                      <p className="text-xs text-neutral-400 mt-2">Monto: {money(o.amount, cur)}</p>
                      {raffle?.qr_tickets && (
                        <div className="mt-3 flex flex-col items-center rounded-xl bg-neutral-50 border border-neutral-200 p-4" data-testid={`lookup-qr-${o.order_id}`}>
                          <QRCodeCanvas value={`${window.location.origin}/t/${o.order_id}`} size={140} bgColor="#fafafa" />
                          <a href={`/t/${o.order_id}`} target="_blank" rel="noreferrer" className="text-xs font-semibold mt-2" style={{ color: brand }}>Ver mi ticket</a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "comprar" && isActive && (
          <>
            <div className="mt-7">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white" style={{ background: brand }}><Ticket size={16} /></div>
                <Label className="font-display font-extrabold text-lg">Elige tus números</Label>
              </div>
              <p className="text-sm text-neutral-500 mt-1">Precio unitario: <b>{money(raffle.price_per_number, cur)}</b></p>
              {(minp > 1 || maxp) && (
                <p className="text-xs text-neutral-400 mt-1" data-testid="minmax-hint">
                  {minp > 1 ? `Compra mínima: ${minp}` : ""}{minp > 1 && maxp ? " · " : ""}{maxp ? `máxima: ${maxp}` : ""}
                </p>
              )}

              {/* Mode toggle */}
              <div className="grid grid-cols-2 gap-2 mt-4 p-1 rounded-xl bg-neutral-100">
                <button data-testid="mode-auto" onClick={() => switchMode("auto")}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all"
                  style={mode === "auto" ? { background: "#fff", color: brand, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" } : { color: "#737373" }}>
                  <Wand2 size={16} /> Al azar
                </button>
                <button data-testid="mode-manual" onClick={() => switchMode("manual")}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all"
                  style={mode === "manual" ? { background: "#fff", color: brand, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" } : { color: "#737373" }}>
                  <Grid3x3 size={16} /> Elegir yo
                </button>
              </div>

              {mode === "auto" && (
                <>
                  {raffle.packages?.length > 0 && (
                    <div className="grid grid-cols-2 gap-2.5 mt-4">
                      {raffle.packages.map((pk, i) => {
                        const active = qty === pk.quantity;
                        return (
                          <button key={pk.label || `pk-${pk.quantity}`} data-testid={`package-${i}`} onClick={() => selectPackage(pk.quantity)}
                            className="text-left rounded-2xl border-2 p-3.5 transition-all hover:scale-[1.02] active:scale-95"
                            style={active ? { background: brand, borderColor: brand, color: "#fff" } : { borderColor: `${brand}30`, background: `${brand}0A` }}>
                            <p className="font-display font-extrabold text-lg">{pk.quantity} <span className="text-xs font-medium opacity-80">números</span></p>
                            <p className={`text-sm font-semibold ${active ? "text-white/90" : ""}`} style={active ? {} : { color: brand }}>{money(pk.price, cur)}</p>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-4 rounded-2xl p-3.5" style={{ background: `${brand}0F` }}>
                    <span className="text-sm font-semibold">Cantidad</span>
                    <div className="flex items-center gap-3">
                      <Button variant="outline" size="icon" data-testid="qty-minus" onClick={() => { setQty(Math.max(minp, qty - 1)); setPicked([]); }} className="rounded-full h-10 w-10 border-2"><Minus size={16} /></Button>
                      <span data-testid="qty-value" className="font-display font-extrabold text-xl w-8 text-center">{qty}</span>
                      <Button variant="outline" size="icon" data-testid="qty-plus" onClick={() => { setQty(maxp ? Math.min(maxp, qty + 1) : qty + 1); setPicked([]); }} className="rounded-full h-10 w-10 border-2"><Plus size={16} /></Button>
                    </div>
                  </div>

                  <Button data-testid="shuffle-btn" onClick={() => doPick(picked.length > 0)} disabled={picking || (picked.length > 0 && reshuffles >= raffle.max_reshuffles)}
                    variant="outline" className="w-full mt-3 rounded-xl h-11 border-2 font-semibold" style={{ borderColor: `${brand}40`, color: brand }}>
                    {picking ? <Loader2 size={18} className="animate-spin" /> : <><Shuffle size={18} className="mr-2" />{picked.length === 0 ? "Selección aleatoria" : `Volver a tirar (${reshuffles}/${raffle.max_reshuffles})`}</>}
                  </Button>
                </>
              )}

              {mode === "manual" && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Toca para elegir tu número</p>
                    <div className="flex items-center gap-3 text-[11px] text-neutral-400">
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block border border-neutral-200 bg-white" /> Disponible</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: brand }} /> Elegido</span>
                    </div>
                  </div>
                  {gridLoading ? (
                    <div className="py-10 flex justify-center"><Loader2 className="animate-spin" style={{ color: brand }} /></div>
                  ) : grid ? (
                    <>
                      {grid.total === 0 ? (
                        <div className="py-10 text-center text-sm text-neutral-500" data-testid="grid-empty">
                          ¡Todos los números ya tienen dueño! 🎉
                        </div>
                      ) : (
                        <AnimatePresence mode="wait">
                          <motion.div key={gridPage}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                            className="grid grid-cols-5 sm:grid-cols-8 gap-1.5" data-testid="number-grid">
                            {grid.numbers.map((it, i) => {
                              const selected = picked.includes(it.n);
                              const lite = grid.numbers.length > 150;
                              if (lite) {
                                return (
                                  <button key={it.n} data-testid={`grid-cell-${it.n}`}
                                    onClick={() => toggleNum(it.n, it.status)}
                                    className={`aspect-square flex items-center justify-center text-xs font-semibold rounded-md border transition-colors duration-150 active:scale-95 ${selected ? "shadow" : "hover:brightness-95"}`}
                                    style={numState(it.status, selected)}>
                                    {padNum(it.n)}
                                  </button>
                                );
                              }
                              return (
                                <motion.button key={it.n} data-testid={`grid-cell-${it.n}`}
                                  layout
                                  initial={{ opacity: 0, scale: 0.85 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ duration: 0.2, delay: Math.min(i * 0.008, 0.25), ease: "easeOut" }}
                                  whileTap={{ scale: 0.92 }}
                                  onClick={() => toggleNum(it.n, it.status)}
                                  className={`aspect-square flex items-center justify-center text-xs font-semibold rounded-md border transition-colors duration-200 ${selected ? "shadow" : "hover:brightness-95"}`}
                                  style={numState(it.status, selected)}>
                                  {padNum(it.n)}
                                </motion.button>
                              );
                            })}
                          </motion.div>
                        </AnimatePresence>
                      )}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-3">
                          <Button variant="outline" size="sm" data-testid="grid-prev" disabled={gridPage === 0} onClick={() => loadGrid(gridPage - 1)} className="rounded-lg transition-transform active:scale-90"><ChevronLeft size={16} /></Button>
                          <span className="text-xs text-neutral-500">Página {gridPage + 1} de {totalPages}</span>
                          <Button variant="outline" size="sm" data-testid="grid-next" disabled={gridPage >= totalPages - 1} onClick={() => loadGrid(gridPage + 1)} className="rounded-lg transition-transform active:scale-90"><ChevronRight size={16} /></Button>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}

              {picked.length > 0 && (
                <div className="mt-4 rounded-2xl border-2 p-4" data-testid="picked-numbers" style={{ borderColor: `${brand}25`, background: `${brand}08` }}>
                  <p className="text-xs font-semibold text-neutral-500 mb-2 uppercase tracking-wide">Tus números ({picked.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {[...picked].sort((a, b) => a - b).map((n) => <span key={n} className="text-sm font-bold px-3 py-1.5 rounded-lg text-white shadow-sm" style={{ background: brand }}>#{padNum(n)}</span>)}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {tab === "comprar" && !isActive && raffle.status !== "drawn" && (
          <div className="mt-6 rounded-2xl bg-neutral-50 p-6 text-center text-neutral-500">Este evento no está recibiendo compras en este momento.</div>
        )}

        <div className="mt-6 rounded-2xl border border-neutral-200 p-4 flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${brand}15`, color: brand }}><Trophy size={18} /></div>
          <div>
            <p className="font-semibold text-sm">Método del ganador</p>
            <p className="text-sm text-neutral-500 mt-0.5">{drawMethodLabel}</p>
            {raffle.draw_rule_text && <p className="text-xs text-neutral-500 mt-1.5 bg-neutral-50 rounded-lg p-2 whitespace-pre-line">{raffle.draw_rule_text}</p>}
          </div>
        </div>

        {(raffle.payment_info || raffle.sinpe_number) && (
          <div className="mt-4 rounded-2xl border-2 p-4" style={{ borderColor: `${brand}25`, background: `${brand}08` }} data-testid="payment-info-public">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={18} style={{ color: brand }} />
              <p className="font-semibold text-sm">¿Cómo pagar?</p>
            </div>
            {raffle.payment_info && <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{raffle.payment_info}</p>}
            {raffle.sinpe_number && <CopyableSinpe value={raffle.sinpe_number} brand={brand} />}
          </div>
        )}

        {raffle.rules_text && (
          <div className="mt-4 rounded-2xl border border-neutral-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <ScrollText size={18} style={{ color: brand }} />
              <p className="font-semibold text-sm">Reglas y políticas</p>
            </div>
            <p className="text-sm text-neutral-600 leading-relaxed whitespace-pre-line">{raffle.rules_text}</p>
          </div>
        )}

        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          {[{ i: ShieldCheck, t: "Pago verificado" }, { i: FileCheck, t: "Comprobante seguro" }, { i: CheckCircle2, t: "Evento transparente" }].map((c, k) => {
            const Ic = c.i;
            return (
              <div key={k} className="rounded-xl p-3" style={{ background: `${brand}0A` }}>
                <Ic size={18} className="mx-auto mb-1" style={{ color: brand }} />
                <p className="text-[11px] font-medium text-neutral-600 leading-tight">{c.t}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating WhatsApp */}
      {waDigits && (
        <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
          aria-label="Contactar por WhatsApp"
          className={`fixed z-30 right-4 sm:right-[calc(50%-14rem)] h-12 w-12 p-3 rounded-full bg-[#25D366] text-white shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-all ${tab === "comprar" && isActive ? "bottom-28" : "bottom-6"}`}>
          <MessageCircle size={22} fill="white" />
        </a>
      )}

      {tab === "comprar" && isActive && (
        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-lg bg-white/95 backdrop-blur border-t border-neutral-100 p-4 z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          <Button data-testid="buy-tickets-btn" onClick={openCheckout} disabled={picking || (mode === "manual" && picked.length === 0)}
            className="w-full h-13 py-6 rounded-full text-base font-bold text-white hover:scale-[1.01] active:scale-95 transition-transform shadow-lg disabled:opacity-60"
            style={{ background: `linear-gradient(135deg, ${brand}, ${brand}D0)` }}>
            {picking ? <Loader2 className="animate-spin" /> : (mode === "manual" && picked.length === 0)
              ? "Selecciona tus números"
              : <>Comprar {ctaCount} {ctaCount === 1 ? "número" : "números"} · {money(priceFor(ctaCount), cur)}</>}
          </Button>
        </div>
      )}

      <Drawer open={!!drawerStep} onOpenChange={(o) => !o && closeDrawer()}>
        <DrawerContent className="max-w-lg mx-auto">
          {drawerStep === "checkout" && (
            <div className="p-5 pb-8 max-h-[85vh] overflow-y-auto">
              <DrawerHeader className="px-0 pt-2"><DrawerTitle className="font-display font-extrabold text-xl">Finaliza tu compra</DrawerTitle></DrawerHeader>

              <div className="rounded-2xl p-3.5 mb-4" style={{ background: `${brand}0C` }}>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Tus números ({picked.length})</p>
                <div className="flex flex-wrap gap-1.5">
                  {[...picked].sort((a, b) => a - b).map((n) => <span key={n} className="text-xs font-bold px-2 py-1 rounded text-white" style={{ background: brand }}>#{padNum(n)}</span>)}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5">
                  <span className="text-sm text-neutral-500">Total a pagar</span>
                  <span className="font-display font-extrabold text-xl" style={{ color: brand }}>{money(priceFor(picked.length), cur)}</span>
                </div>
              </div>

              {mirrors.length > 0 && (
                <div className="rounded-2xl border-2 border-dashed p-3.5 mb-4" data-testid="mirror-panel"
                  style={{ borderColor: `${brand}45`, background: `${brand}08` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Repeat size={16} style={{ color: brand }} />
                    <p className="font-semibold text-sm">¿También quieres el número invertido?</p>
                  </div>
                  <p className="text-xs text-neutral-500 mb-3">Están libres y puedes agregarlos a este mismo pedido, o ignorarlos.</p>
                  <div className="space-y-2">
                    {mirrors.map((m) => {
                      const added = picked.includes(m.mirror);
                      return (
                        <div key={m.mirror} className="flex items-center justify-between gap-2 rounded-xl bg-white border border-neutral-200 px-3 py-2">
                          <span className="flex items-center gap-2 text-sm">
                            <span className="font-bold text-neutral-400">#{padNum(m.number)}</span>
                            <ChevronRight size={14} className="text-neutral-300" />
                            <span className="font-bold text-white px-2 py-0.5 rounded" style={{ background: brand }}>#{padNum(m.mirror)}</span>
                          </span>
                          <Button data-testid={`mirror-toggle-${m.mirror}`} onClick={() => toggleMirror(m.mirror)}
                            size="sm" variant={added ? "outline" : "default"}
                            className="rounded-lg h-8 text-xs font-semibold max-sm:h-10 max-sm:px-4"
                            style={added ? { color: brand, borderColor: `${brand}55` } : { background: brand, color: "#fff" }}>
                            {added ? <><X size={13} className="mr-1" />Quitar</> : <><Plus size={13} className="mr-1" />Agregar</>}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(raffle.payment_info || raffle.sinpe_number) && (
                <div className="rounded-2xl border-2 p-3.5 mb-4" style={{ borderColor: `${brand}30`, background: `${brand}0A` }} data-testid="payment-info-checkout">
                  <div className="flex items-center gap-2 mb-1.5"><Wallet size={16} style={{ color: brand }} /><p className="font-semibold text-sm">Realiza tu pago a:</p></div>
                  {raffle.payment_info && <p className="text-sm text-neutral-700 whitespace-pre-line leading-relaxed">{raffle.payment_info}</p>}
                  {raffle.sinpe_number && <CopyableSinpe value={raffle.sinpe_number} brand={brand} />}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Nombre completo <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input data-testid="buyer-name" value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })} className="rounded-lg pl-9" placeholder="Ej: María Rodríguez" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Correo electrónico <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input data-testid="buyer-email" type="email" value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })} className="rounded-lg pl-9" placeholder="correo@ejemplo.com" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Teléfono / WhatsApp <span className="text-red-500">*</span></Label>
                  <div className="relative mt-1">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input data-testid="buyer-phone" value={buyer.buyer_phone} onChange={(e) => setBuyer({ ...buyer, buyer_phone: e.target.value })} className="rounded-lg pl-9" placeholder="8888-8888" />
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Comprobante de pago <span className="text-red-500">*</span></Label>
                  <label data-testid="proof-dropzone" className="mt-1 flex items-center gap-3 border-2 border-dashed rounded-xl p-4 cursor-pointer transition-colors"
                    style={{ borderColor: file ? brand : "#d4d4d4", background: file ? `${brand}0A` : "transparent" }}>
                    <input type="file" accept="image/*,application/pdf" data-testid="proof-input" className="hidden"
                      onChange={(e) => setFile(e.target.files[0])} />
                    <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: file ? brand : "#f5f5f5", color: file ? "#fff" : "#a3a3a3" }}>
                      {file ? <FileCheck size={20} /> : <Upload size={20} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{file ? file.name : "Sube tu comprobante (obligatorio)"}</p>
                      <p className="text-xs text-neutral-400">{file ? "Toca para cambiar" : "Imagen o PDF"}</p>
                    </div>
                    {file && <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); }} className="text-neutral-400 hover:text-red-500"><X size={18} /></button>}
                  </label>
                  <p className="text-xs text-neutral-400 mt-1.5">Sin el comprobante no se puede finalizar la compra.</p>
                </div>
              </div>

              <label className="flex items-start gap-2.5 mt-4 cursor-pointer max-sm:gap-3 max-sm:py-2" data-testid="buy-terms-wrap">
                <Checkbox data-testid="buy-accept-terms" checked={acceptedTerms} onCheckedChange={(v) => setAcceptedTerms(!!v)} className="mt-0.5 max-sm:h-5 max-sm:w-5" />
                <span className="text-xs text-neutral-600 leading-relaxed max-sm:text-sm">He leído y acepto los <a href="/terminos" target="_blank" rel="noreferrer" className="font-semibold underline" style={{ color: brand }}>Términos y Condiciones</a></span>
              </label>
              <Button data-testid="finalize-purchase-btn" onClick={finalizePurchase} disabled={submitting || !acceptedTerms}
                className="w-full h-13 py-6 rounded-full text-white font-bold mt-4 shadow-lg disabled:opacity-60" style={{ background: `linear-gradient(135deg, ${brand}, ${brand}D0)` }}>
                {submitting ? <Loader2 className="animate-spin" /> : <>Finalizar compra · {money(priceFor(picked.length), cur)}</>}
              </Button>
            </div>
          )}
          {drawerStep === "done" && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${brand}18`, color: brand }}><CheckCircle2 size={32} /></div>
              <h3 className="font-display font-extrabold text-xl">¡Compra registrada!</h3>
              <p className="text-neutral-500 mt-2 text-sm">El organizador verificará tu pago y recibirás un correo de confirmación. Puedes consultar tu estado en "Validar mis números".</p>
              {raffle?.qr_tickets && order?.order_id && (
                <div className="mt-5 flex flex-col items-center rounded-2xl bg-neutral-50 border border-neutral-200 p-5" data-testid="done-qr">
                  <QRCodeCanvas value={`${window.location.origin}/t/${order.order_id}`} size={160} bgColor="#fafafa" />
                  <p className="text-xs text-neutral-500 mt-3">Guarda este QR para validar tus números</p>
                  <a href={`/t/${order.order_id}`} target="_blank" rel="noreferrer" className="text-xs font-semibold mt-1" style={{ color: brand }} data-testid="done-ticket-link">Ver mi ticket</a>
                </div>
              )}
              <Button onClick={closeDrawer} data-testid="done-btn" className="mt-6 rounded-full w-full h-12 text-white font-bold" style={{ background: brand }}>Entendido</Button>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      {!raffle?.white_label && (
        <footer className="p-5 text-center border-t border-neutral-100 mt-4">
          <span className="inline-flex items-center gap-1.5 text-xs text-neutral-400"><ShieldCheck size={14} /> Evento gestionado con MCTECH EVENTOS</span>
        </footer>
      )}
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-neutral-50">{children}</div>;
}
